<template>
  <nav
    class="PublicSiteNavBar z-[1000] top-0 sticky border-b-2 border-primary-light bg-white"
    :class="{ '!bg-danger': userSwitcher.switchedToMate }"
  >
    <div
      v-if="topItems.length"
      class="TopItems !bg-primary w-full max-lg:hidden text-sm"
    >
      <ul
        class="flex flex-row justify-end max-w-7xl mx-auto gap-3 items-center"
      >
        <NavBarItem
          v-for="({ click, ...props }, index) in topItems"
          :key="index"
          v-bind="props"
          type="inverted"
          v-on="click ? { click } : {}"
        />
        <LocaleSelector class="max-lg:hidden" type="inverted" />
      </ul>
    </div>
    <ul
      class="NavBarItems flex flex-row justify-between max-w-7xl mx-auto gap-3 items-center max-lg:hidden p-1"
      :class="{ 'h-16': $branding.isPriceOffer }"
    >
      <NavBarItem :to="localePath({ name: 'indexPage' })">
        <JoszakiLogo />
      </NavBarItem>
      <NavBarItem
        v-if="userSwitcher.switchedToMate"
        :text="$t('navbar.switchBackToAdmin')"
        type="primary"
        @click="switchBackToAdmin"
      />
      <NavBarItem
        v-if="$branding.hasFeature(FEATURES.HEADER_SEARCH)"
        class="flex-1"
      >
        <AutocompleteSearch />
      </NavBarItem>
      <li v-else class="flex-1" />
      <NavBarItem
        v-for="{ click, ...props } in mainItems"
        :key="props.id"
        class="max-lg:hidden"
        v-bind="props"
        v-on="click ? { click } : {}"
      />
    </ul>
    <ul class="MobileNavBar lg:hidden flex flex-row gap-2 p-1">
      <NavBarItem
        :to="localePath({ name: 'indexPage' })"
        :class="{ 'flex-1': !showMobileSearch }"
      >
        <JoszakiLogo :hide-label="showMobileSearch" />
      </NavBarItem>
      <NavBarItem
        v-if="userSwitcher.switchedToMate"
        :text="$t('navbar.switchBackToAdmin')"
        type="primary"
        @click="switchBackToAdmin"
      />
      <template v-if="!userSwitcher.switchedToMate">
        <NavBarItem
          v-if="$branding.hasFeature(FEATURES.HEADER_SEARCH)"
          v-show="showMobileSearch"
          :class="{ 'flex-1': showMobileMenu }"
          @focus="showMobileMenu = false"
        >
          <AutocompleteSearch />
        </NavBarItem>
        <NavBarItem
          type="primary"
          :icon="showMobileSearch ? 'angle-left' : 'search'"
          @click="showMobileSearch = !showMobileSearch"
        />
        <NavBarItem
          v-show="!showMobileSearch && !showMobileMenu"
          :text="$t('navbar.tenderButton')"
          type="primary"
          @click="requestTender({ source: 'header' })"
        />
      </template>
      <NavBarItem
        class="self-center"
        icon="bars"
        @click="showMobileMenu = !showMobileMenu"
      />
    </ul>
    <ul
      :class="{ 'max-lg:hidden': !showMobileMenu }"
      class="MobileNavBarDropdown lg:hidden flex-col flex-1 gap-2 absolute w-full bg-white border-b-2 left-0 top-full border-primary-light px-2 overflow-scroll max-h-[75vh]"
    >
      <NavBarItem
        v-for="({ click, ...props }, index) in topItems"
        :key="`top-${index}`"
        v-bind="props"
        v-on="click ? { click } : {}"
      />
      <LocaleSelector />
      <NavBarItem
        v-for="({ click, ...props }, index) in mainItems"
        :key="`center-${index}`"
        v-bind="props"
        v-on="click ? { click } : {}"
      />
    </ul>
  </nav>
</template>

<script>
import { ref, computed, watch } from "vue";
import { useContext, useRoute } from "@nuxtjs/composition-api";
import NavBarItem from "./NavBarItem.vue";
import JoszakiLogo from "@/components/_refactored/img/JoszakiLogo.vue";
import AutocompleteSearch from "~/components/_refactored/header/AutocompleteSearch";
import LocaleSelector from "~/components/_refactored/common/LocaleSelector.vue";
import { useRequestTender } from "~/composables/requestTender";
import { useAdminUserSwitcher } from "~/components/_refactored/admin";
import { useUserMenu } from "~/components/_refactored/header/useUserMenu";
import { usePopularProfessions } from "~/composables/usePopularProfessions";
import { usePopularProfessionTasks } from "~/composables/usePopularProfessionTasks";
import { useProfessionNavLinks } from "~/composables/useProfessionNavLinks";
import { FEATURES } from "~/config/pages/features.config";
import { useSettingsStore } from "~/stores/settings";

export default {
  components: {
    JoszakiLogo,
    NavBarItem,
    AutocompleteSearch,
    LocaleSelector,
  },
  setup() {
    const showMobileMenu = ref(false);
    const showMobileSearch = ref(false);
    const { i18n, localePath, $branding } = useContext();
    const route = useRoute();

    const { userMenu } = useUserMenu();
    const userSwitcher = useAdminUserSwitcher();

    const { requestTender } = useRequestTender();

    const { professionMenuItems } = usePopularProfessions();
    const { professionTaskMenuItems } = usePopularProfessionTasks();
    const { professionMenu, pricesMenu } = useProfessionNavLinks();

    const settingsStore = useSettingsStore();

    watch(route, () => {
      showMobileMenu.value = false;
      showMobileSearch.value = false;
    });

    const mainItems = computed(() => {
      const items = [];

      if ($branding.hasFeature(FEATURES.JOB_MARKET)) {
        items.push({
          id: "request-tender",
          text: i18n.t("navbar.tenderButton"),
          click: () => requestTender({ source: "header" }),
          type: "primary",
        });
      }

      if ($branding.hasFeature(FEATURES.PROFESSIONALS)) {
        items.push({
          id: "professionals",
          text:
            professionMenu.value?.text ?? i18n.t("navbar.professions.label"),
          icon: "briefcase",
          items: professionMenu.value?.items ?? professionMenuItems.value,
        });
      }

      if ($branding.hasFeature(FEATURES.PRICES)) {
        items.push({
          id: "prices",
          text:
            pricesMenu.value?.text ?? i18n.t("navbar.professionTasks.label"),
          icon: "dollar-sign",
          items: pricesMenu.value?.items ?? professionTaskMenuItems.value,
        });
      }

      if ($branding.hasFeature(FEATURES.QUESTIONS)) {
        items.push({
          id: "questions",
          text: i18n.t("navbar.questionsPage"),
          to: localePath({ name: "questionsPage" }),
          icon: "question-circle",
        });
      }

      if ($branding.isPriceOffer) {
        items.push({
          id: "try-price-offer",
          text: i18n.t("navbar.tryPriceOffer"),
          to: localePath({ name: "demoPriceOffersIndexPage" }),
        });

        items.push({
          id: "price-offer-prices",
          text: i18n.t("navbar.priceOfferPrices"),
          to: localePath({ name: "priceOfferIndexPage", hash: "#prices" }),
        });
      }

      items.push(...userMenu.value);
      return items;
    });

    const topItems = computed(() => {
      const items = [];

      if ($branding.isPriceOffer) {
        if ($branding.hasFeature(FEATURES.ABOUT_US)) {
          items.push({
            id: "about",
            text: i18n.t("navbar.about"),
            to: localePath({ name: "aboutUs" }),
          });
        }

        if ($branding.hasFeature(FEATURES.BLOG)) {
          items.push({
            id: "blog",
            text: i18n.t("navbar.blog"),
            to: localePath({ name: "blogLandingPage" }),
          });
        }

        if (settingsStore.contactForm) {
          items.push({
            id: "contact",
            text: i18n.t("navbar.contact"),
            to: settingsStore.contactForm,
            target: "_blank",
          });
        }
      }

      return items;
    });

    const switchBackToAdmin = async () => {
      await userSwitcher.switchBackToAdmin();
    };

    return {
      ...useRequestTender,
      showMobileMenu,
      showMobileSearch,
      mainItems,
      topItems,
      userSwitcher,
      switchBackToAdmin,
      requestTender,
      FEATURES,
    };
  },
};
</script>
